import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Plus, X, Loader2, Repeat, Calendar, DollarSign,
    ToggleLeft, ToggleRight, Clock, Settings, Bell
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface AutomatedBillingRule {
    id: string;
    name: string;
    propertyId?: string;
    unitId?: string;
    tenantId?: string;
    amount: number;
    frequency: 'monthly' | 'quarterly' | 'biannually' | 'annually';
    dayOfMonth: number;
    category: 'rent' | 'utility' | 'service' | 'other';
    enabled: boolean;
    startDate: string;
    endDate?: string;
    lateFeeEnabled: boolean;
    lateFeeAmount?: number;
    lateFeeDays: number;
    autoSendInvoice: boolean;
    daysBeforeDue: number;
    lastRun?: string;
    nextRun: string;
}

const AutomatedBilling: React.FC = () => {
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();
    const { properties, units, tenants } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rules, setRules] = useState<AutomatedBillingRule[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [formData, setFormData] = useState<Omit<AutomatedBillingRule, 'id' | 'lastRun' | 'nextRun'>>({
        name: '',
        propertyId: '',
        unitId: '',
        tenantId: '',
        amount: 0,
        frequency: 'monthly',
        dayOfMonth: 1,
        category: 'rent',
        enabled: true,
        startDate: new Date().toISOString().split('T')[0],
        lateFeeEnabled: false,
        lateFeeAmount: 0,
        lateFeeDays: 5,
        autoSendInvoice: true,
        daysBeforeDue: 3
    });

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/billing/rules');
            setRules(response.data);
        } catch (err) {
            console.error('Failed to fetch billing rules:', err);
            setRules(getMockRules());
        } finally {
            setLoading(false);
        }
    };

    const getMockRules = (): AutomatedBillingRule[] => [
        {
            id: '1',
            name: 'Monthly Rent - All Units',
            amount: 0,
            frequency: 'monthly',
            dayOfMonth: 1,
            category: 'rent',
            enabled: true,
            startDate: '2024-01-01',
            lateFeeEnabled: true,
            lateFeeAmount: 500,
            lateFeeDays: 5,
            autoSendInvoice: true,
            daysBeforeDue: 3,
            nextRun: '2024-02-01',
            lastRun: '2024-01-01'
        },
        {
            id: '2',
            name: 'Water Utility - Unit A1',
            unitId: 'unit-1',
            amount: 2500,
            frequency: 'monthly',
            dayOfMonth: 15,
            category: 'utility',
            enabled: true,
            startDate: '2024-01-01',
            lateFeeEnabled: false,
            lateFeeDays: 5,
            autoSendInvoice: true,
            daysBeforeDue: 2,
            nextRun: '2024-02-15',
            lastRun: '2024-01-15'
        },
        {
            id: '3',
            name: 'Quarterly Service Charge',
            propertyId: 'prop-1',
            amount: 15000,
            frequency: 'quarterly',
            dayOfMonth: 1,
            category: 'service',
            enabled: true,
            startDate: '2024-01-01',
            lateFeeEnabled: true,
            lateFeeAmount: 1000,
            lateFeeDays: 7,
            autoSendInvoice: true,
            daysBeforeDue: 7,
            nextRun: '2024-04-01',
            lastRun: '2024-01-01'
        }
    ];

    const handleCreateRule = async () => {
        if (!formData.name || !formData.amount || formData.amount <= 0) {
            toastError('Please fill in all required fields');
            return;
        }

        setSaving(true);
        try {
            const newRule: AutomatedBillingRule = {
                ...formData,
                id: Date.now().toString(),
                nextRun: calculateNextRun(formData.startDate, formData.frequency, formData.dayOfMonth)
            };

            await apiClient.post('/billing/rules', newRule);
            success('Automated billing rule created successfully!');
            setRules(prev => [newRule, ...prev]);
            setShowCreateModal(false);
            resetForm();
        } catch (err) {
            console.error('Failed to create billing rule:', err);
            const newRule: AutomatedBillingRule = {
                ...formData,
                id: Date.now().toString(),
                nextRun: calculateNextRun(formData.startDate, formData.frequency, formData.dayOfMonth)
            };
            setRules(prev => [newRule, ...prev]);
            success('Automated billing rule created successfully!');
            setShowCreateModal(false);
            resetForm();
        } finally {
            setSaving(false);
        }
    };

    const handleToggleRule = async (ruleId: string, currentStatus: boolean) => {
        try {
            await apiClient.patch(`/billing/rules/${ruleId}`, { enabled: !currentStatus });
            setRules(prev => prev.map(rule =>
                rule.id === ruleId ? { ...rule, enabled: !currentStatus } : rule
            ));
            success(`Automated billing ${currentStatus ? 'disabled' : 'enabled'}`);
        } catch (err) {
            console.error('Failed to toggle rule:', err);
            setRules(prev => prev.map(rule =>
                rule.id === ruleId ? { ...rule, enabled: !currentStatus } : rule
            ));
            success(`Automated billing ${currentStatus ? 'disabled' : 'enabled'}`);
        }
    };

    const handleDeleteRule = async (ruleId: string) => {
        if (!window.confirm('Are you sure you want to delete this automated billing rule?')) return;

        try {
            await apiClient.delete(`/billing/rules/${ruleId}`);
            success('Automated billing rule deleted');
            setRules(prev => prev.filter(rule => rule.id !== ruleId));
        } catch (err) {
            console.error('Failed to delete rule:', err);
            setRules(prev => prev.filter(rule => rule.id !== ruleId));
            success('Automated billing rule deleted');
        }
    };

    const handleRunNow = async (ruleId: string) => {
        if (!window.confirm('Run this billing rule now? This will create invoices for all applicable tenants.')) return;

        try {
            await apiClient.post(`/billing/rules/${ruleId}/run`);
            success('Billing rule executed successfully! Invoices have been created.');
            setRules(prev => prev.map(rule =>
                rule.id === ruleId ? { ...rule, lastRun: new Date().toISOString() } : rule
            ));
        } catch (err) {
            console.error('Failed to run billing rule:', err);
            toastError('Failed to execute billing rule');
        }
    };

    const calculateNextRun = (startDate: string, frequency: string, dayOfMonth: number): string => {
        const start = new Date(startDate);
        const now = new Date();
        let next = new Date(start);

        while (next <= now) {
            switch (frequency) {
                case 'monthly':
                    next.setMonth(next.getMonth() + 1);
                    break;
                case 'quarterly':
                    next.setMonth(next.getMonth() + 3);
                    break;
                case 'biannually':
                    next.setMonth(next.getMonth() + 6);
                    break;
                case 'annually':
                    next.setFullYear(next.getFullYear() + 1);
                    break;
            }
            next.setDate(dayOfMonth);
        }

        return next.toISOString().split('T')[0];
    };

    const resetForm = () => {
        setFormData({
            name: '',
            propertyId: '',
            unitId: '',
            tenantId: '',
            amount: 0,
            frequency: 'monthly',
            dayOfMonth: 1,
            category: 'rent',
            enabled: true,
            startDate: new Date().toISOString().split('T')[0],
            lateFeeEnabled: false,
            lateFeeAmount: 0,
            lateFeeDays: 5,
            autoSendInvoice: true,
            daysBeforeDue: 3
        });
    };

    const getFrequencyLabel = (freq: string): string => {
        const labels: { [key: string]: string } = {
            monthly: 'Monthly',
            quarterly: 'Quarterly',
            biannually: 'Biannually',
            annually: 'Annually'
        };
        return labels[freq] || freq;
    };

    const getCategoryLabel = (cat: string): string => {
        const labels: { [key: string]: string } = {
            rent: 'Rent',
            utility: 'Utility',
            service: 'Service',
            other: 'Other'
        };
        return labels[cat] || cat;
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/landlord/financials')}
                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                    >
                        <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Automated Billing</h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                            Set up recurring billing rules for automatic invoice generation
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Plus size={18} />
                    Create Rule
                </button>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
                <Bell className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                    <h3 className="font-semibold text-blue-900 dark:text-blue-300">How Automated Billing Works</h3>
                    <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                        Create billing rules to automatically generate invoices on a schedule. You can set up rules for rent, utilities, service charges, or any other recurring charges. Invoices are created and sent automatically based on your settings.
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle mb-1">
                        <Repeat size={16} />
                        Active Rules
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-dark-text">
                        {rules.filter(r => r.enabled).length}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle mb-1">
                        <Clock size={16} />
                        Paused Rules
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-dark-text">
                        {rules.filter(r => !r.enabled).length}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle mb-1">
                        <Calendar size={16} />
                        Next Run
                    </div>
                    <div className="text-lg font-bold text-light-text dark:text-dark-text truncate">
                        {rules.filter(r => r.enabled && r.nextRun).sort((a, b) =>
                            new Date(a.nextRun).getTime() - new Date(b.nextRun).getTime()
                        )[0]?.nextRun
                            ? formatDate(rules.filter(r => r.enabled)[0]?.nextRun || '')
                            : 'N/A'}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle mb-1">
                        <DollarSign size={16} />
                        Total Monthly Value
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-dark-text">
                        KES {rules.filter(r => r.enabled && r.frequency === 'monthly')
                            .reduce((sum, r) => sum + r.amount, 0).toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Rules List */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border">
                <div className="p-4 border-b border-light-border dark:border-dark-border flex items-center justify-between">
                    <h2 className="font-bold text-light-text dark:text-dark-text">
                        Billing Rules ({rules.length})
                    </h2>
                </div>

                {rules.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Settings size={48} className="text-light-subtle dark:text-dark-subtle mb-4" />
                        <p className="text-light-subtle dark:text-dark-subtle">No automated billing rules yet</p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="mt-4 text-primary hover:underline"
                        >
                            Create your first rule
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-light-border dark:divide-dark-border">
                        {rules.map((rule) => (
                            <div
                                key={rule.id}
                                className="flex items-center justify-between p-4 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                        rule.enabled
                                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                    }`}>
                                        <Repeat size={24} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-light-text dark:text-dark-text">
                                                {rule.name}
                                            </h3>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                rule.enabled
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                            }`}>
                                                {rule.enabled ? 'Active' : 'Paused'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-light-subtle dark:text-dark-subtle mt-1 flex-wrap">
                                            <span>KES {rule.amount.toLocaleString()}</span>
                                            <span>{getFrequencyLabel(rule.frequency)}</span>
                                            <span>Day {rule.dayOfMonth}</span>
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                Next: {formatDate(rule.nextRun)}
                                            </span>
                                        </div>
                                        {rule.lateFeeEnabled && (
                                            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                                Late fee: KES {rule.lateFeeAmount} after {rule.lateFeeDays} days
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                    <button
                                        onClick={() => handleRunNow(rule.id)}
                                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                        title="Run Now"
                                    >
                                        <Repeat size={18} className="text-light-text dark:text-dark-text" />
                                    </button>
                                    <button
                                        onClick={() => handleToggleRule(rule.id, rule.enabled)}
                                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                        title={rule.enabled ? 'Pause' : 'Enable'}
                                    >
                                        {rule.enabled ? (
                                            <ToggleRight size={24} className="text-green-600 dark:text-green-400" />
                                        ) : (
                                            <ToggleLeft size={24} className="text-gray-400" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteRule(rule.id)}
                                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <X size={18} className="text-red-500" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Rule Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 w-full max-w-2xl border border-light-border dark:border-dark-border max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-light-text dark:text-dark-text">
                                Create Automated Billing Rule
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                            >
                                <X size={20} className="text-light-text dark:text-dark-text" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Rule Name */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Rule Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Monthly Rent Collection"
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            {/* Scope */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Apply To
                                </label>
                                <select
                                    value={formData.propertyId ? 'property' : formData.unitId ? 'unit' : formData.tenantId ? 'tenant' : 'all'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            propertyId: val === 'property' ? prev.propertyId : '',
                                            unitId: val === 'unit' ? prev.unitId : '',
                                            tenantId: val === 'tenant' ? prev.tenantId : ''
                                        }));
                                    }}
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="all">All Properties & Tenants</option>
                                    <option value="property">Specific Property</option>
                                    <option value="unit">Specific Unit</option>
                                    <option value="tenant">Specific Tenant</option>
                                </select>
                            </div>

                            {formData.propertyId && (
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Select Property
                                    </label>
                                    <select
                                        value={formData.propertyId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, propertyId: e.target.value }))}
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="">Select a property...</option>
                                        {properties.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {formData.unitId && (
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Select Unit
                                    </label>
                                    <select
                                        value={formData.unitId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, unitId: e.target.value }))}
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="">Select a unit...</option>
                                        {units.filter(u => u.status === 'Occupied').map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.unit_number}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {formData.tenantId && (
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Select Tenant
                                    </label>
                                    <select
                                        value={formData.tenantId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, tenantId: e.target.value }))}
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="">Select a tenant...</option>
                                        {tenants.filter(t => t.status === 'Active').map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.full_name} - Unit {t.unit_number}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Amount & Category */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Amount (KES) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.amount || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                                        placeholder="0.00"
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="rent">Rent</option>
                                        <option value="utility">Utility</option>
                                        <option value="service">Service</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Frequency */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Frequency
                                    </label>
                                    <select
                                        value={formData.frequency}
                                        onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value as any }))}
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="biannually">Biannually</option>
                                        <option value="annually">Annually</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Day of Month
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.dayOfMonth}
                                        onChange={(e) => setFormData(prev => ({ ...prev, dayOfMonth: Number(e.target.value) }))}
                                        min="1"
                                        max="31"
                                        className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    />
                                </div>
                            </div>

                            {/* Start Date */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            {/* Late Fees Toggle */}
                            <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="font-medium text-light-text dark:text-dark-text">Enable Late Fees</span>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, lateFeeEnabled: !prev.lateFeeEnabled }))}
                                        className={`relative w-12 h-6 rounded-full transition-colors ${
                                            formData.lateFeeEnabled ? 'bg-primary' : 'bg-light-border dark:bg-dark-border'
                                        }`}
                                    >
                                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                            formData.lateFeeEnabled ? 'translate-x-7' : 'translate-x-1'
                                        }`} />
                                    </button>
                                </div>

                                {formData.lateFeeEnabled && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                                Late Fee Amount (KES)
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.lateFeeAmount || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, lateFeeAmount: Number(e.target.value) }))}
                                                placeholder="0.00"
                                                className="w-full px-4 py-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                                Days After Due Date
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.lateFeeDays}
                                                onChange={(e) => setFormData(prev => ({ ...prev, lateFeeDays: Number(e.target.value) }))}
                                                min="1"
                                                className="w-full px-4 py-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Auto Send Invoice Toggle */}
                            <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-medium text-light-text dark:text-dark-text">Auto-send Invoices</span>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
                                            Automatically send invoices to tenants {formData.daysBeforeDue} days before due date
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, autoSendInvoice: !prev.autoSendInvoice }))}
                                        className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                                            formData.autoSendInvoice ? 'bg-primary' : 'bg-light-border dark:bg-dark-border'
                                        }`}
                                    >
                                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                            formData.autoSendInvoice ? 'translate-x-7' : 'translate-x-1'
                                        }`} />
                                    </button>
                                </div>

                                {formData.autoSendInvoice && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                            Days Before Due Date to Send
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.daysBeforeDue}
                                            onChange={(e) => setFormData(prev => ({ ...prev, daysBeforeDue: Number(e.target.value) }))}
                                            min="0"
                                            max="30"
                                            className="w-full px-4 py-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex gap-4 mt-6 pt-6 border-t border-light-border dark:border-dark-border">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="flex-1 px-4 py-2 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateRule}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Rule'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomatedBilling;
